import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Gift, TrendingUp, Users, CheckCircle, ArrowRight, DollarSign,
  Briefcase, Shield, Copy, Star
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EarningsTable({ isAr }) {
  const rows = [
    { amount: 'AED 1,000,000', fee: 'AED 3,000', context: isAr ? 'إحالة واحدة = 3,000 درهم' : '1 referral = AED 3K' },
    { amount: 'AED 2,000,000', fee: 'AED 6,000', context: isAr ? 'شقة قياسية في أبوظبي' : 'Standard Abu Dhabi apartment' },
    { amount: 'AED 3,500,000', fee: 'AED 10,500', context: isAr ? 'فيلا متوسطة / شقة دبي' : 'Mid-range villa / Dubai apt' },
    { amount: 'AED 5,000,000', fee: 'AED 15,000', context: isAr ? 'عقار فاخر' : 'Premium property' },
    { amount: 'AED 10,000,000', fee: 'AED 30,000', context: isAr ? 'بنتهاوس / تجاري' : 'Penthouse / commercial' },
    { amount: '5 × AED 2M/mo', fee: 'AED 30,000/mo', context: isAr ? 'دخل سلبي قابل للتوسع' : 'Scalable passive income', highlight: true },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-[#0B2F44] text-white">
            <th className="px-5 py-3.5 text-start text-sm font-semibold">{isAr ? 'مبلغ التمويل' : 'Mortgage Amount'}</th>
            <th className="px-5 py-3.5 text-start text-sm font-semibold">{isAr ? 'رسوم الإحالة (0.3%)' : 'Referral Fee (0.3%)'}</th>
            <th className="px-5 py-3.5 text-start text-sm font-semibold">{isAr ? 'السياق' : 'Context'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={`border-b border-[#D0D8DC]/30 ${row.highlight ? 'bg-[#C9A84C]/5' : i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFB]'}`}>
              <td className="px-5 py-3.5 text-sm font-medium text-[#0B2F44]" style={{ fontFamily: 'var(--font-en-mono)' }}>{row.amount}</td>
              <td className="px-5 py-3.5">
                <span className={`text-sm font-bold ${row.highlight ? 'text-[#C9A84C]' : 'text-[#0B2F44]'}`} style={{ fontFamily: 'var(--font-en-mono)' }}>
                  {row.fee}
                </span>
              </td>
              <td className="px-5 py-3.5 text-sm text-[#4A6572]">{row.context}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegistrationForm({ isAr, displayFont }) {
  const [form, setForm] = useState({
    full_name: '', company_name: '', professional_type: '', id_number: '',
    bank_details: '', whatsapp: '', email: '', how_heard: '', agreed: false
  });
  const [status, setStatus] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);

  const profTypes = isAr
    ? ['وسيط عقاري', 'مستشار استثماري', 'محاسب', 'محامي', 'تأسيس أعمال', 'مدير لايف ستايل', 'أخرى']
    : ['Real Estate Broker', 'Investment Consultant', 'Accountant', 'Lawyer', 'Business Setup', 'Lifestyle Manager', 'Other'];

  const heardFrom = isAr
    ? ['جوجل', 'وسائل التواصل', 'إحالة صديق', 'فعالية', 'أخرى']
    : ['Google', 'Social Media', 'Friend Referral', 'Event', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agreed) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/referrals`, {
        full_name: form.full_name,
        company_name: form.company_name,
        professional_type: form.professional_type,
        id_number: form.id_number,
        bank_details: form.bank_details,
        whatsapp: form.whatsapp,
        email: form.email,
        how_heard: form.how_heard
      });
      setReferralCode(data.referral_code);
      setStatus('success');
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div data-testid="referral-success" className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-[#1A7A4A] mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-[#0B2F44] mb-2" style={{ fontFamily: displayFont }}>
          {isAr ? 'تم التسجيل بنجاح!' : 'Registration Successful!'}
        </h3>
        <p className="text-[#4A6572] mb-6">{isAr ? 'كود الإحالة الخاص بك:' : 'Your referral code:'}</p>
        <div className="inline-flex items-center gap-3 bg-[#0B2F44] rounded-xl px-6 py-4 mb-6">
          <span className="text-2xl font-bold text-[#C9A84C]" style={{ fontFamily: 'var(--font-en-mono)' }}>{referralCode}</span>
          <button onClick={() => navigator.clipboard?.writeText(referralCode)} className="text-white/60 hover:text-white">
            <Copy className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#4A6572]">
          {isAr ? 'سيصلك تأكيد على واتساب + حزمة الترحيب خلال ساعة.' : 'WhatsApp confirmation + welcome pack will be sent within 1 hour.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="referral-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'الاسم الكامل' : 'Full Name'} *</label>
          <Input data-testid="ref-name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]" required />
        </div>
        <div>
          <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'اسم الشركة' : 'Company Name'}</label>
          <Input data-testid="ref-company" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'النوع المهني' : 'Professional Type'} *</label>
          <Select value={form.professional_type} onValueChange={v => setForm({...form, professional_type: v})}>
            <SelectTrigger data-testid="ref-type" className="h-[52px] border-[#D0D8DC]"><SelectValue placeholder={isAr ? 'اختر...' : 'Select...'} /></SelectTrigger>
            <SelectContent>{profTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'رقم الهوية أو جواز السفر' : 'Emirates ID / Passport'} *</label>
          <Input data-testid="ref-id" value={form.id_number} onChange={e => setForm({...form, id_number: e.target.value})} className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]" required />
        </div>
      </div>
      <div>
        <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'تفاصيل الحساب البنكي (AED)' : 'Bank Account Details (AED)'} *</label>
        <Input data-testid="ref-bank" value={form.bank_details} onChange={e => setForm({...form, bank_details: e.target.value})} className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]" required placeholder={isAr ? 'IBAN / اسم البنك' : 'IBAN / Bank Name'} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'رقم واتساب' : 'WhatsApp Number'} *</label>
          <Input data-testid="ref-whatsapp" value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]" required />
        </div>
        <div>
          <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'البريد الإلكتروني' : 'Email'} *</label>
          <Input data-testid="ref-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="h-[52px] border-[#D0D8DC] focus:border-[#A68966]" required />
        </div>
      </div>
      <div>
        <label className="text-sm text-[#4A6572] block mb-1.5">{isAr ? 'كيف سمعت عن جُليد؟' : 'How did you hear about Guleed Mortgage?'}</label>
        <Select value={form.how_heard} onValueChange={v => setForm({...form, how_heard: v})}>
          <SelectTrigger className="h-[52px] border-[#D0D8DC]"><SelectValue placeholder={isAr ? 'اختر...' : 'Select...'} /></SelectTrigger>
          <SelectContent>{heardFrom.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex items-start gap-3 pt-2">
        <Checkbox
          data-testid="ref-agree"
          checked={form.agreed}
          onCheckedChange={v => setForm({...form, agreed: v})}
          className="mt-0.5 border-[#D0D8DC] data-[state=checked]:bg-[#A68966] data-[state=checked]:border-[#A68966]"
        />
        <label className="text-sm text-[#4A6572] leading-relaxed">
          {isAr ? 'أوافق على شروط وأحكام برنامج الإحالة وسياسة الخصوصية.' : 'I agree to the Referral Program Terms & Conditions and Privacy Policy.'}
        </label>
      </div>

      {status === 'error' && <p className="text-[#C0392B] text-sm">{isAr ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Try again.'}</p>}

      <Button type="submit" data-testid="ref-submit" disabled={loading || !form.agreed} className="w-full bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold py-6 text-base">
        {loading ? (isAr ? 'جاري التسجيل...' : 'Registering...') : (isAr ? 'سجّل الآن وابدأ الكسب' : 'Register Now & Start Earning')}
        <ArrowRight className="w-5 h-5 ms-2" />
      </Button>
    </form>
  );
}

export default function ReferralPage() {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const displayFont = isAr ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = isAr ? 'var(--font-ar-body)' : 'var(--font-en-body)';

  const benefits = [
    { icon: DollarSign, title: isAr ? '0.3% من كل صفقة' : '0.3% Per Deal', desc: isAr ? 'اكسب 0.3% من قيمة كل تمويل عقاري تحيله' : 'Earn 0.3% of every mortgage amount you refer' },
    { icon: TrendingUp, title: isAr ? 'دخل قابل للتوسع' : 'Scalable Income', desc: isAr ? 'لا حدود — كلما أحلت أكثر، كسبت أكثر' : 'No ceiling — the more you refer, the more you earn' },
    { icon: Shield, title: isAr ? 'دفع مضمون وسريع' : 'Guaranteed Fast Payment', desc: isAr ? 'تحويل بنكي مباشر بعد صرف التمويل' : 'Direct bank transfer after mortgage disbursement' },
    { icon: Briefcase, title: isAr ? 'لوحة تحكم خاصة' : 'Dedicated Dashboard', desc: isAr ? 'تتبع إحالاتك وأرباحك في الوقت الفعلي' : 'Track your referrals and earnings in real-time' },
  ];

  return (
    <div data-testid="referral-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0B2F44] to-[#071E2C] pt-28 pb-20 lg:pb-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 mb-6">
                <Gift className="w-4 h-4 text-[#C9A84C]" />
                <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-wider">{isAr ? 'برنامج الشركاء' : 'Partner Program'}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: displayFont, lineHeight: isAr ? '1.4' : '1.15' }}>
                {isAr ? (
                  <>اكسب حتى <span className="text-[#C9A84C]">30,000 درهم</span> من كل إحالة</>
                ) : (
                  <>Earn Up to <span className="text-[#C9A84C]">AED 30,000</span> Per Referral</>
                )}
              </h1>
              <p className="text-white/60 text-base max-w-lg leading-relaxed mb-8" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.8' }}>
                {isAr
                  ? 'انضم لبرنامج إحالة جُليد واكسب 0.3% من كل تمويل عقاري تحيله. لا حاجة لرخصة عقارية — فقط شبكة علاقاتك.'
                  : 'Join Guleed Mortgage\'s referral program and earn 0.3% of every mortgage you refer. No real estate license needed — just your network.'}
              </p>
              <div className="flex items-center gap-6 text-white/40 text-sm">
                <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#C9A84C]" /> {isAr ? '50+ شريك نشط' : '50+ Active Partners'}</span>
                <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#C9A84C]" /> {isAr ? 'تسجيل في دقيقتين' : '2-Min Registration'}</span>
              </div>
            </div>

            {/* Earnings Preview */}
            <div className="bg-[#0D3A54]/60 backdrop-blur-sm rounded-2xl p-8 border border-[#C9D6D9]/10">
              <h3 className="text-white font-bold text-lg mb-5" style={{ fontFamily: displayFont }}>
                {isAr ? 'هيكل العمولات' : 'Earnings Structure'}
              </h3>
              <EarningsTable isAr={isAr} />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#F8FAFB] py-16 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0B2F44] text-center mb-12" style={{ fontFamily: displayFont }}>
            {isAr ? 'لماذا تنضم لبرنامج جُليد؟' : 'Why Join Guleed Mortgage\'s Program?'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {benefits.map((b, i) => (
              <div key={i} data-testid={`ref-benefit-${i}`} className="bg-white rounded-2xl p-7 border border-[#D0D8DC]/50 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-center group">
                <div className="w-14 h-14 rounded-xl bg-[#0B2F44] flex items-center justify-center mx-auto mb-5 group-hover:bg-[#C9A84C] transition-colors">
                  <b.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-[#0B2F44] mb-2">{b.title}</h3>
                <p className="text-sm text-[#4A6572]">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* Registration Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 lg:p-10 border border-[#D0D8DC]/50 shadow-sm">
              <h3 className="text-xl font-bold text-[#0B2F44] mb-2 text-center" style={{ fontFamily: displayFont }}>
                {isAr ? 'سجّل كشريك إحالة' : 'Register as a Referral Partner'}
              </h3>
              <p className="text-sm text-[#4A6572] text-center mb-8">
                {isAr ? 'التسجيل مجاني ويستغرق أقل من دقيقتين' : 'Free registration — takes less than 2 minutes'}
              </p>
              <RegistrationForm isAr={isAr} displayFont={displayFont} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
